import React, { useEffect, useRef, useState } from 'react';
import { SubscriptionUpdatePaymentModal } from '@fff-web/fff-payments';
import { useUserContext } from '@fff-web/fff-utilities';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { trackEvent, trackPageLoad, identify } from '@fff-web/fff-analytics';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import Layout from '../../components/Layout';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';
import useScreenMode from '../../hooks/useScreenMode';
import { DESKTOP, Plans } from '../constants';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import AllErrorMessage from '../../components/AllErrorMessage';
import env from '../MyAccount/utils/env';
import { updateSubscription } from '../../services/subscription';
import { getPlanChangeCompletedEventStatusFromResponse } from '../../helpers';
import { sendActivateSegmentEventAfterResponse } from '../../Events';

import './styles.css';
import { redirectToBaseSite } from '../../utils/redirectMethods';

const TrialUpgrade = ({ history, flags, ldClient }) => {
  const trialCaptchaRef = useRef(null);
  const [selected, setSelected] = useState(Plans.SEASONAL);
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const isTrial = userInfo && userInfo.user && userInfo.user.subscriptionType === 'TRIAL';
  const handleChange = (membership) => setSelected(membership);
  const screenMode = useScreenMode();
  const thanksPage = '/trial-upgrade-thanks';

  useEffect(() => {
    if (isTrial) {
      trackPageLoad({
        pathName: '/activate-membership',
        properties: {
          title: 'Trial Subscription Page',
        },
      }).then();
    }
  }, []);

  const {
    isLoading,
    setLoading,
    showRetry,
    showPayment,
    errorPaymentMessage,
    onClose,
    handlePlanChangeResponse,
  } = useShowErrorOrRedirectThankYouPageV2(history, thanksPage);

  if (!isTrial) {
    redirectToBaseSite();
    return null;
  }

  const handleChangeSubscription = async () => {
    trialCaptchaRef.current.resetCaptcha();
    const upgradingPlan = Plans.ANNUAL === selected ? Plans.ANNUAL : Plans.SEASONAL;
    await trackEvent({
      ctaName: 'upgrade-confirmation',
      properties: {
        upgrading_plan: upgradingPlan,
      },
    });
    trialCaptchaRef.current
      .execute({ async: true })
      .then(({ response: captchaToken }) => {
        setLoading(true);
        handleActivateSubscription(captchaToken, upgradingPlan);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error processing you request:', err);
      });
  };

  const handleActivateSubscription = (captchaToken, upgradingPlan) => {
    let response = null;

    // make call to activate subscription
    updateSubscription(upgradingPlan, userInfo?.user?.planCode, [], captchaToken)
      .then((updateSubscriptionResponse) => {
        response = updateSubscriptionResponse;
        const status = getPlanChangeCompletedEventStatusFromResponse(response);
        const eventsToFire = [
          sendActivateSegmentEventAfterResponse(status, 'Confirm Purchase', 'Trial'),
        ];
        if (status === 'success') {
          eventsToFire.push(identify({ traits: { planCode: upgradingPlan } }));
        }
        return Promise.resolve(eventsToFire);
      })
      .then(() => {
        handlePlanChangeResponse(response);
      });
  };

  const { displayAdditionalPerks } = flags;

  const hCaptcha = () => (
    <HCaptcha
      sitekey={env.hCaptchaSiteKey}
      onVerify={() => undefined}
      size="invisible"
      ref={trialCaptchaRef}
    />
  );

  return (
    <Layout
      isLoading={isLoading}
      showError={showRetry}
      ErrorComponent={() => <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />}
    >
      {screenMode !== DESKTOP ? (
        <MobileLayout
          handleChange={handleChange}
          selected={selected}
          showAdditionalPerks={displayAdditionalPerks}
          onClickUpgrade={handleChangeSubscription}
        >
          {hCaptcha()}
        </MobileLayout>
      ) : (
        <DesktopLayout
          handleChangeActive={handleChange}
          active={selected}
          showAdditionalPerks={displayAdditionalPerks}
          onClickUpgrade={handleChangeSubscription}
        >
          {hCaptcha()}
        </DesktopLayout>
      )}

      <SubscriptionUpdatePaymentModal
        onClose={onClose}
        subtitle={errorPaymentMessage}
        isOpen={showPayment}
        onSuccess={handleChangeSubscription}
        hasError={!!errorPaymentMessage}
      />
    </Layout>
  );
};

export default withLDConsumer()(TrialUpgrade);
